import { Box, Stack, TextField, Typography, useTheme } from '@mui/material';
import { useFormik } from 'formik';
import { isUndefined, min } from 'lodash';
import { AlertCircle } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import IconCredit from 'src/asset/icons/icon-credit.svg';
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import CreditCostIndicator from 'src/components/common/CreditCostIndicator';
import FormikForm from 'src/components/common/FormikForm';
import IconFeather from 'src/components/common/Icon';
import { IExportTradeDataTableProps } from 'src/components/ExportData';
import ModalComponent from 'src/components/Modal/ModalComponent';
import { StyledHeaderAlert } from 'src/features/PeopleProfiles/PopupExportPeople';
import { useFeatureAccess } from 'src/hooks/useFeatureAccess';
import { useGetRemainCredit } from 'src/hooks/useGetPointsFeature';
import { useQueryParams } from 'src/hooks/useQueryParam';
import useRefetch from 'src/hooks/useRefetch';
import { useMutateExportDataTableTradeData } from 'src/services/mutate';
import { QUERY_KEY_USAGE } from 'src/services/queries';
import { MAXIMUM_RECORDS_EXPORT_TRADE_DATA_TABS } from 'src/utils/constants/config';
import { FeatureAction } from 'src/utils/constants/featuresPoint';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { SUCCESS_CODE } from 'src/utils/constants/statusCode';
import { getFormItemError, renderFormItemError } from 'src/utils/helpers/renderFormItemError';
import { renderNumberOrDefault } from 'src/utils/helpers/stringHelpers';
import { getPageSizeBaseOnTotalRecords } from 'src/utils/helpers/utilities';
import { NotifyType, useNotifyCreditCosts } from 'src/zustand/useNotifyCreditCosts';
import useSnackbarStore from 'src/zustand/useSnackbarStore';
import * as Yup from 'yup';

interface IProps extends IExportTradeDataTableProps {
  open: boolean;
  handleClose: VoidFunction;
  fileType: string;
  totalRecordsAvailable: number;
}

const PopupExportData = (props: IProps) => {
  const theme = useTheme();
  const {
    open,
    handleClose,
    featureId = '',
    fileType,
    fileName,
    endpoint = '',
    totalRecordsAvailable,
    paramsExport = {},
  } = props;

  const { handleOpenNotify } = useNotifyCreditCosts((state) => state);
  const { mutateAsync, isPending } = useMutateExportDataTableTradeData();
  const navigate = useNavigate();
  const { hs_code } = useQueryParams();
  const { refetchQuery } = useRefetch();
  const { dispatch } = useSnackbarStore();
  const { getFeaturePoints } = useFeatureAccess();
  const creditCosts = getFeaturePoints(endpoint, FeatureAction.VIEW);
  const remainCredit = useGetRemainCredit();
  const formik = useFormik({
    validateOnChange: true,
    enableReinitialize: true,
    initialValues: {
      from_record: 1,
      to_record: min([10, totalRecordsAvailable]),
    },
    validationSchema: Yup.object().shape({
      from_record: Yup.number().required('This field is required').min(1, 'From must be at least 1'),
      to_record: Yup.number()
        .required('This field is required')
        .min(1, 'To must be at least 1')
        .max(totalRecordsAvailable, `We just have ${renderNumberOrDefault(totalRecordsAvailable)} records available`)
        .test('to_record_valid', 'Must be greater than or equal to From', (text?: number, context?: ExpectedAny) => {
          const recordFrom = context?.parent?.from_record || 1;
          const recordTo = text || 1;
          return recordFrom <= recordTo;
        })
        .test(
          'total_record_limit',
          `You can only export a maximum of ${renderNumberOrDefault(MAXIMUM_RECORDS_EXPORT_TRADE_DATA_TABS)} at a time`,
          (text?: number, context?: ExpectedAny) => {
            const recordFrom = context?.parent?.from_record || 1;
            const recordTo = text || 0;
            return recordTo - recordFrom + 1 <= MAXIMUM_RECORDS_EXPORT_TRADE_DATA_TABS;
          },
        ),
    }),
    onSubmit: async (values) => {
      const totalRecords = values.to_record - values.from_record + 1;
      const res = await mutateAsync({
        url: endpoint,
        params: {
          ...paramsExport,
          from_record: values.from_record,
          to_record: values.to_record,
          page_size: getPageSizeBaseOnTotalRecords(totalRecords),
          // keyword: '',
          // hs_code,
        },
      });
      if (res?.status !== SUCCESS_CODE.GET) {
        dispatch({
          title: 'Export Failed',
          open: true,
          severity: 'error',
          description:
            'The export process encountered an error and could not be completed. Please review your data and try again. If the issue persists, contact support.',
        });
      }
      // Tạo URL từ `Blob`
      const url = URL.createObjectURL(res.data);
      // Tạo một thẻ `<a>` tạm thời để kích hoạt tải file
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName || 'adamftd-export-data'}.${fileType}`; // Tên file CSV được tải về
      // Thêm thẻ `a` vào DOM và kích hoạt sự kiện click
      document.body.appendChild(a);
      a.click();
      // Sau khi tải xong, xóa thẻ `a` và URL object
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      handleClose();
      refetchQuery([QUERY_KEY_USAGE]);
    },
  });

  const handleExport = () => {
    if (isUndefined(creditCosts?.planName) || getTotalCredits() > remainCredit) {
      handleOpenNotify({
        type: NotifyType.NotEnoughCredit,
        showCloseIcon: true,
      });
      return;
    }

    formik.submitForm();
  };

  const getTotalRecord = () => {
    if (formik.values.to_record && formik.values.from_record && formik.values.to_record >= formik.values.from_record) {
      return formik.values.to_record - formik.values.from_record + 1;
    }
    return 0;
  };

  const getTotalCredits = () => {
    if (formik.values.to_record >= formik.values.from_record) {
      return (
        (formik.values.to_record - formik.values.from_record + 1) * creditCosts.creditCosts
      );
    }
    return 0;
  };

  return (
    <ModalComponent
      headerAlert={
        creditCosts?.planName ? (
          <StyledHeaderAlert
            id="form-contribute-header-alert"
            direction="row"
            alignItems="center"
            bgcolor={theme.palette.info.light}
          >
            <IconFeather
              icon={<AlertCircle />}
              muiProps={{
                color: 'info',
                fontSize: 'small',
              }}
            />
            <Typography variant="caption2" color="info.main" ml={1}>
              The export action can take up to 1 minute, thank you for your patience.
            </Typography>
          </StyledHeaderAlert>
        ) : (
          <StyledHeaderAlert
            id="form-contribute-header-alert"
            direction="row"
            alignItems="center"
            bgcolor={theme.palette.success.light}
          >
            <Box src={IconCredit} component="img" width={24} />
            <Typography variant="caption2" ml={1}>
              You do not have credits yet.{' '}
              <Typography
                variant="caption2"
                component="span"
                color="secondary.main"
                fontWeight={600}
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(AuthRouteKeys.Pricing)}
              >
                Please choose a suitable Plan.
              </Typography>
            </Typography>
          </StyledHeaderAlert>
        )
      }
      styleBox={{ width: 600 }}
      handleClose={handleClose}
      open={open}
    >
      <FormikForm formik={formik}>
        <Stack mt={2} justifyContent={'center'} spacing={2}>
          <Stack spacing={1} width={540}>
            <Typography textAlign="center" variant="h5">
              Export Option
            </Typography>
            <Typography textAlign="center" color={'text.secondary'} variant="body2">
              You can export a maximum of {renderNumberOrDefault(MAXIMUM_RECORDS_EXPORT_TRADE_DATA_TABS)} records at a
              time.
            </Typography>
            <Box px={2} py={1} bgcolor={theme.palette.background.paper1} borderRadius={theme.shape.borderRadius / 2}>
              <Typography textAlign="left" variant="subtitle2">
                Available records: {renderNumberOrDefault(totalRecordsAvailable)}
              </Typography>
            </Box>
          </Stack>
          <Stack spacing={2} direction={'row'}>
            <Box width="100%">
              <TextField
                id="from_record"
                name="from_record"
                label="From"
                placeholder="eg: 1, 10, 200, ..."
                value={formik.values.from_record}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                type="number"
                required
                size="small"
                fullWidth
                error={Boolean(getFormItemError('from_record', formik))}
              />
              {renderFormItemError(getFormItemError('from_record', formik))}
            </Box>
            <Box width="100%">
              <TextField
                id="to_record"
                name="to_record"
                label="To"
                placeholder="eg: 1, 10, 200, ..."
                value={formik.values.to_record}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                type="number"
                required
                size="small"
                fullWidth
                error={Boolean(getFormItemError('from_record', formik))}
              />
              {renderFormItemError(getFormItemError('to_record', formik))}
            </Box>
          </Stack>
          <Box>
            <Typography variant="subtitle2" align="center">
              Total Records To Export
            </Typography>
            <Typography variant="h4" align="center" color="primary.main" mt={1}>
              {getTotalRecord()}
            </Typography>
          </Box>
          <ButtonWithLoading
            variant="contained"
            color="primary"
            size="large"
            isRounded
            onClick={handleExport}
            isLoading={isPending}
            disabled={isPending || !formik.isValid}
            startIcon={
              <CreditCostIndicator
                pointData={{
                  creditCosts: getTotalCredits(),
                  planName: creditCosts.planName,
                }}
              />
            }
          >
            Export
          </ButtonWithLoading>
        </Stack>
      </FormikForm>
    </ModalComponent>
  );
};

export default PopupExportData;
